import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, IndianRupee, Loader2, Pencil, XCircle } from "lucide-react";
import { Select } from "antd";

import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input } from "../../components/ui/input";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { PERK_CRITERIA, PERK_PAYOUT, PERK_PAYOUT_PREVIEW } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

// Mirrors payroll/constants.PERK_PAYOUT_METHOD. The wording spells out what each
// one does to the money, because "points" in particular is not self-evident.
const METHOD = [
    { value: "flat", label: "Same amount for everyone" },
    { value: "percent", label: "Percentage of pay" },
    { value: "points", label: "In proportion to the score" },
    { value: "manual", label: "Enter each amount myself" },
];

const METHOD_HINT = {
    flat: "Everyone on the list gets the same figure.",
    percent: "Each person gets a percentage of their own pay, so the amount scales with salary.",
    points: "The maximum goes to a full score, and everyone else gets their share of it — whoever scored 90 out of 110 gets 82% of the maximum.",
    manual: "Type a figure against each person. Use this when the amounts were agreed case by case.",
};

const BASE = [
    { value: "gross", label: "Gross" },
    { value: "basic", label: "Basic" },
    { value: "ctc", label: "CTC" },
];

const STATUS_CHIP = {
    pending: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
};

const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PerkPayout = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetch, create, update } = useApiMethods();

    const [criteria, setCriteria] = useState(null);
    const [existing, setExisting] = useState([]);
    const [extras, setExtras] = useState({});
    const [loading, setLoading] = useState(true);

    const [method, setMethod] = useState("points");
    const [amount, setAmount] = useState("");
    const [rate, setRate] = useState("");
    const [base, setBase] = useState("gross");
    const [manualAmounts, setManualAmounts] = useState({});

    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmGenerate, setConfirmGenerate] = useState(false);
    const [adjusting, setAdjusting] = useState(null);
    const [adjustAmount, setAdjustAmount] = useState("");
    const [adjustReason, setAdjustReason] = useState("");
    const [cancelling, setCancelling] = useState(null);

    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Perk Criteria", "SubMenu");

    const load = useCallback(async () => {
        setLoading(true);
        const [detail, payouts] = await Promise.all([
            fetch(PERK_CRITERIA, { id }),
            fetch(PERK_PAYOUT, { id, page_size: 500 }),
        ]);
        setLoading(false);
        if (detail?.status === "success") setCriteria(detail.data);
        if (payouts?.status === "success") {
            setExisting(payouts.data?.result || []);
            setExtras(payouts.data?.extras || {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const params = useMemo(() => {
        if (method === "percent") return { method, rate, base };
        if (method === "manual") return { method, amounts: manualAmounts };
        return { method, amount };
    }, [method, amount, rate, base, manualAmounts]);

    const runPreview = async () => {
        setBusy(true);
        setPreview(null);
        // 'manual' has nothing to preview until amounts are typed, and they are
        // typed against the preview rows — so it is seeded from a zero-amount
        // flat preview to get the list of people first.
        const query =
            method === "manual"
                ? { id, method: "flat", amount: 0 }
                : { id, ...params, amounts: undefined };
        const response = await fetch(PERK_PAYOUT_PREVIEW, query);
        setBusy(false);
        if (response?.status === "success") {
            setPreview(response.data);
        } else {
            ToastMsg("error", response?.message || "Could not work out the amounts");
        }
    };

    const generate = async () => {
        setBusy(true);
        const body = { id, ...params };
        if (method === "manual") body.amounts = manualAmounts;
        const response = await create(PERK_PAYOUT, body);
        setBusy(false);
        setConfirmGenerate(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setPreview(null);
            load();
        } else {
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Could not generate the payouts");
        }
    };

    const submitAdjust = async () => {
        setBusy(true);
        const response = await update(PERK_PAYOUT, {
            payout_id: adjusting.id,
            amount: adjustAmount,
            reason: adjustReason,
        });
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setAdjusting(null);
            setAdjustAmount("");
            setAdjustReason("");
            load();
        } else {
            ToastMsg("error", response?.message || "Could not change the amount");
        }
    };

    const submitCancel = async () => {
        setBusy(true);
        const response = await update(PERK_PAYOUT, { payout_id: cancelling.id, cancel: true });
        setBusy(false);
        setCancelling(null);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            load();
        } else {
            ToastMsg("error", response?.message || "Could not cancel the payout");
        }
    };

    // Manual amounts are typed against the previewed rows, so the running total
    // has to come from what has been typed rather than from the server preview.
    const manualTotal = useMemo(
        () => Object.values(manualAmounts).reduce((sum, v) => sum + (Number(v) || 0), 0),
        [manualAmounts],
    );
    const previewTotal = method === "manual" ? manualTotal : preview?.summary?.total || 0;
    const previewCount =
        method === "manual"
            ? Object.values(manualAmounts).filter((v) => Number(v) > 0).length
            : preview?.summary?.employees || 0;

    const canGenerate =
        editPermission &&
        (method === "manual" ? manualTotal > 0 : !!preview && previewTotal > 0);

    if (loading) {
        return (
            <ContainerCard>
                <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading
                </div>
            </ContainerCard>
        );
    }

    const hasPaid = existing.some((p) => p.status === "paid");

    return (
        <ContainerCard>
            <div className="p-3">
                <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-2 mb-1 text-muted-foreground"
                    onClick={() => navigate(`/payroll/perkcriteria/${id}/review`)}
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to the shortlist
                </Button>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Pay {criteria?.name || "the shortlist"}
                </h1>
                <p className="text-sm text-muted-foreground">
                    {criteria?.candidate_count ?? 0} qualified by the criteria · paid as a{" "}
                    {criteria?.perk_type === "incentive" ? "Incentive" : "Bonus"} line on the next
                    payroll run, with tax handled there
                </p>
            </div>

            {/* Method */}
            <div className="mx-3 mb-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-2 sm:col-span-2">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            How the amount is worked out
                        </label>
                        <Select
                            className="w-full"
                            size="large"
                            value={method}
                            options={METHOD}
                            onChange={(value) => {
                                setMethod(value);
                                setPreview(null);
                            }}
                            disabled={busy || !editPermission}
                        />
                        <p className="text-[11px] text-muted-foreground">{METHOD_HINT[method]}</p>
                    </div>

                    {(method === "flat" || method === "points") && (
                        <div className="space-y-2">
                            <label htmlFor="amount" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {method === "flat" ? "Amount each" : "Maximum for a full score"}{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="amount"
                                type="number"
                                min="0"
                                step="1"
                                value={amount}
                                onChange={(e) => {
                                    setAmount(e.target.value);
                                    setPreview(null);
                                }}
                                placeholder="20000"
                                disabled={busy || !editPermission}
                            />
                        </div>
                    )}

                    {method === "percent" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                                <label htmlFor="rate" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Percentage <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    id="rate"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={rate}
                                    onChange={(e) => {
                                        setRate(e.target.value);
                                        setPreview(null);
                                    }}
                                    placeholder="8.33"
                                    disabled={busy || !editPermission}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Of</label>
                                <Select
                                    className="w-full"
                                    size="large"
                                    value={base}
                                    options={BASE}
                                    onChange={(value) => {
                                        setBase(value);
                                        setPreview(null);
                                    }}
                                    disabled={busy || !editPermission}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" onClick={runPreview} disabled={busy || !editPermission}>
                        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                        {method === "manual" ? "List the people" : "Work out the cost"}
                    </Button>
                    {preview && (
                        <span className="text-sm">
                            <b className="tabular-nums">{previewCount}</b> employee
                            {previewCount === 1 ? "" : "s"} ·{" "}
                            <b className="tabular-nums">₹ {money(previewTotal)}</b> in total
                        </span>
                    )}
                </div>

                {/* A zero row means the method could not produce a figure for that
                    person, not that they were judged unworthy — worth saying so. */}
                {preview?.summary?.zero_amount > 0 && method !== "manual" && (
                    <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2.5">
                        <p className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            {preview.summary.zero_amount} employee(s) work out to nothing — usually no
                            salary on file, or no score to scale by. They are on the list; the method just
                            cannot price them.
                        </p>
                    </div>
                )}
            </div>

            {/* Preview rows */}
            {preview && (
                <div className="mx-3 mb-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                <th className="px-3 py-2 font-medium">Employee</th>
                                <th className="px-3 py-2 font-medium text-right">Score</th>
                                <th className="px-3 py-2 font-medium">Worked out from</th>
                                <th className="px-3 py-2 font-medium text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(preview.result || []).map((row) => (
                                <tr key={row.employee_id} className="border-t border-gray-200 dark:border-gray-700">
                                    <td className="px-3 py-2">
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            {row.employee_name}
                                        </span>
                                        <span className="block text-xs text-muted-foreground">
                                            {row.employee_code}
                                            {row.department ? ` · ${row.department}` : ""}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                        {row.points}/{row.points_possible}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">
                                        {row.basis?.method === "percent"
                                            ? `${row.basis.rate}% of ${row.basis.base} ${money(row.basis.base_amount)}`
                                            : row.basis?.method === "points"
                                            ? row.basis.ratio === null
                                                ? "no score to scale by"
                                                : `${Math.round(row.basis.ratio * 100)}% of ${money(row.basis.maximum)}`
                                            : row.basis?.method === "flat"
                                            ? "same for everyone"
                                            : "entered by hand"}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {method === "manual" ? (
                                            <Input
                                                type="number"
                                                min="0"
                                                step="1"
                                                className="w-28 ml-auto text-right"
                                                value={manualAmounts[row.employee_id] ?? ""}
                                                onChange={(e) =>
                                                    setManualAmounts((prev) => ({
                                                        ...prev,
                                                        [row.employee_id]: e.target.value,
                                                    }))
                                                }
                                                disabled={busy || !editPermission}
                                            />
                                        ) : (
                                            <span className="tabular-nums font-medium">{money(row.amount)}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!preview.result?.length && (
                                <tr>
                                    <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                                        Nobody is on the final list. Check the shortlist review screen.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {preview && (
                <div className="mx-3 mb-4">
                    <Button onClick={() => setConfirmGenerate(true)} disabled={busy || !canGenerate}>
                        <IndianRupee className="h-4 w-4 mr-1" />
                        Approve ₹ {money(previewTotal)} for payment
                    </Button>
                </div>
            )}

            {/* Already generated */}
            {existing.length > 0 && (
                <div className="mx-3 mb-4">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Approved for payment</h2>
                        <span className="text-xs text-muted-foreground tabular-nums">
                            ₹ {money(extras.total)} · {extras.pending || 0} waiting for payroll ·{" "}
                            {extras.paid || 0} paid
                            {extras.cancelled ? ` · ${extras.cancelled} cancelled` : ""}
                        </span>
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 font-medium">Employee</th>
                                    <th className="px-3 py-2 font-medium text-right">Amount</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2 font-medium">Note</th>
                                    <th className="px-3 py-2 font-medium text-right"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {existing.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={cn(
                                            "border-t border-gray-200 dark:border-gray-700",
                                            row.status === "cancelled" && "opacity-60",
                                        )}
                                    >
                                        <td className="px-3 py-2">
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {row.employee_name}
                                            </span>
                                            <span className="block text-xs text-muted-foreground">
                                                {row.employee_code}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                                            {money(row.amount)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span
                                                className={cn(
                                                    "px-2 py-1 text-xs font-medium rounded-full",
                                                    STATUS_CHIP[row.status] || STATUS_CHIP.pending,
                                                )}
                                            >
                                                {row.status === "pending" ? "waiting for payroll" : row.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[240px]">
                                            {row.adjusted_reason || ""}
                                        </td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            {/* A paid figure is immutable: it has gone out on a
                                                payslip somebody already has. */}
                                            {editPermission && row.status === "pending" && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        title="Change the amount"
                                                        className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                                        onClick={() => {
                                                            setAdjusting(row);
                                                            setAdjustAmount(String(row.amount ?? ""));
                                                            setAdjustReason("");
                                                        }}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        title="Cancel this payout"
                                                        className="text-rose-600 hover:bg-rose-50 dark:hover:bg-gray-700"
                                                        onClick={() => setCancelling(row)}
                                                    >
                                                        <XCircle className="h-4 w-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {hasPaid && (
                        <p className="mt-2 text-[11px] text-muted-foreground">
                            Paid amounts cannot be changed — they are on payslips that have already been
                            issued. Correct one with a fresh payout next month.
                        </p>
                    )}
                </div>
            )}

            <ConfirmPopupModal
                open={confirmGenerate}
                onClose={() => setConfirmGenerate(false)}
                onConfirm={generate}
                title={`Approve ₹ ${money(previewTotal)} for ${previewCount} employee${
                    previewCount === 1 ? "" : "s"
                }?`}
                description="The amounts are recorded and picked up by the next payroll run, which credits each one as its own payslip line. Nothing leaves the company until that run. You can still change or cancel an amount until it is paid."
                confirmLabel="Approve"
                type="warning"
                loading={busy}
            />

            <DialogModal
                open={!!adjusting}
                onClose={() => setAdjusting(null)}
                onSave={submitAdjust}
                title={`Change the amount for ${adjusting?.employee_name || ""}`}
                saveLabel="Save"
                closeLabel="Cancel"
                loading={busy}
                width="460px"
            >
                <div className="space-y-3">
                    <div className="space-y-2">
                        <label htmlFor="adjust" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Amount <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="adjust"
                            type="number"
                            min="0"
                            step="1"
                            value={adjustAmount}
                            onChange={(e) => setAdjustAmount(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="adjustReason" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="adjustReason"
                            value={adjustReason}
                            onChange={(e) => setAdjustReason(e.target.value)}
                            placeholder="Agreed with the ward head"
                            maxLength={255}
                            disabled={busy}
                        />
                        <p className="text-[11px] text-muted-foreground">
                            Kept on the record next to this amount, like a shortlist decision.
                        </p>
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!cancelling}
                onClose={() => setCancelling(null)}
                onConfirm={submitCancel}
                title={`Cancel the payout for ${cancelling?.employee_name || ""}?`}
                description="The row is kept and marked cancelled, so the reversal is on the record. Payroll will not credit it."
                confirmLabel="Cancel payout"
                type="danger"
                destructive={true}
                loading={busy}
            />
        </ContainerCard>
    );
};

export default PerkPayout;
